<template>
  <div class="my-contracts-page">
    <div class="my-contracts-header d-flex justify-content-between align-items-center">
      <img v-if="companySettings?.logoUrl" :src="companySettings.logoUrl" alt="Logo" height="40">
      <div class="d-flex align-items-center">
        <span class="me-3">{{ currentUser?.fullName }}</span>
        <button type="button" class="btn btn-grey" @click="handleLogout">
          <i class="os-icon os-icon-signs-11 me-1"></i> Logout
        </button>
      </div>
    </div>

    <div class="element-box my-contracts-content">
      <BackButton v-if="currentUser?.enabled && selectedDocumentId === null" label="Zur Anwendung" to="/"/>

      <h5 class="form-header mb-4">Meine Verträge</h5>

      <ContractDocumentView
          v-if="selectedDocumentId !== null"
          :key="selectedDocumentId"
          :document-id="selectedDocumentId"
          :is-bsg-side="false"
          @back="selectedDocumentId = null"
          @signed="handleSigned"/>

      <template v-else>
        <div v-if="currentUser?.enabled === false" class="alert alert-info">
          <p class="mb-2"><b>Willkommen, {{ currentUser?.firstName }}!</b></p>
          <p class="mb-0" v-if="allSignedByUser">
            Vielen Dank, Sie haben alle Vertragsunterlagen unterschrieben. Ihr Zugang wird freigeschaltet,
            sobald die BSG die Unterlagen ebenfalls unterschrieben hat.
          </p>
          <p class="mb-0" v-else>
            Bitte öffnen Sie die Vertragsunterlagen und unterschreiben Sie diese. Ihr Zugang wird vollständig
            freigeschaltet, sobald alle Unterlagen von Ihnen und von der BSG unterschrieben sind.
          </p>
        </div>

        <Loader v-if="!isLoaded"/>

        <div v-if="isLoaded && contracts.length === 0" class="alert alert-warning mb-0">
          Für Sie sind noch keine Vertragsunterlagen vorhanden.
        </div>

        <ContractList
            :contracts="contracts"
            :is-bsg-side="false"
            @open="selectedDocumentId = $event"/>

      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import router from '@/router';
import {piniaUserStore} from '@/store/piniaUserStore';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {ErrorToast} from '@/components/VueToast';
import BackButton from '@/components/button/BackButton.vue';
import Loader from '@/components/Loader.vue';
import userContractService from '@/api/UserContract';
import ContractDocumentView from '@/views/App/MyContracts/ContractDocumentView.vue';
import ContractList from '@/views/App/MyContracts/ContractList.vue';

// Own layout (no menu, top bar and notification polling): a user without signed contract
// can call only a few API routes, everything else returns 403 "contract_not_signed"
const userStore = piniaUserStore();
const companySettingsStore = piniaCompanySettingsStore();

const currentUser = computed(() => userStore.currentUser.user);
const companySettings = computed(() => companySettingsStore.companySettings);

const contracts = ref<any[]>([]);
const isLoaded = ref(false);
const selectedDocumentId = ref<number | null>(null);

const allSignedByUser = computed(() =>
    contracts.value.length > 0 && contracts.value.every((contract) => contract.signedByUser)
);

async function handleSigned(document: any) {
  // The last signature (BSG signed before) completes the contract and enables the user
  if (document.userEnabled && currentUser.value?.enabled === false) {
    await userStore.piniaGetUser();
  }
  await loadContracts();
}

async function loadContracts() {
  try {
    const {data} = await userContractService.getMyContracts();
    contracts.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isLoaded.value = true;
  }
}

function handleLogout() {
  localStorage.clear();
  window.location.reload();
  router.push({name: 'login'});
}

onMounted(loadContracts);
</script>

<style scoped>
.my-contracts-page {
  min-height: 100vh;
  background-color: #f2f4f8;
  padding: 20px;
}

.my-contracts-header {
  max-width: 1000px;
  margin: 0 auto 20px auto;
}

.my-contracts-content {
  max-width: 1000px;
  margin: 0 auto;
}
</style>
